import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, post } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { MapView } from '../../lib/MapView.js';
import { Alert, fmtDate, shiftLabel, Spinner } from '../../lib/ui.js';
import type { EmploymentType, Shift, ShiftTimes } from '@siva/shared';
import { AdminsPage } from './AdminsPage.js';
import { DesignationsCard } from '../DesignationsCard.js';
import { DateField } from '../../lib/DateField.js';
import { TimeField } from '../../lib/TimeField.js';
import { useConfirm } from '../../lib/Modal.js';
import { t } from '../../i18n/index.js';

type Settings = {
  office_name: string;
  lat: number | null;
  lng: number | null;
  radius_m: number;
  office_public_ip: string | null;
  play_group_url: string | null;
  android_app_required: boolean;
  app_required_for_all: boolean;
  app_install_route: 'download' | 'play_group' | 'play_public';
  timezone: string;
  day_cutoff_time: string;
  digest_time: string;
  daily_report_time: string;
  monthly_report_enabled: boolean;
  punchout_reminder_time: string;
  /** The four shift windows: two shifts across two employment types. */
  shift_times: ShiftTimes;
  off_cap_total: number;
  off_cap_full_time: number;
  off_cap_part_time: number;
  updated_at: string;
};
type Holiday = { id: string; date: string; name: string };

const DEFAULT_CENTER = { lat: 12.9716, lng: 77.5946 };

/**
 * The server names what changed by column — day_cutoff_time, off_cap_full_time — and the
 * saved message used to print those. The owner read "Changed: day_cutoff_time, digest_time,
 * …" on their own settings page. The words are the ones on the form.
 */
const SETTING_LABELS: Record<string, () => string> = {
  office_name: () => t('settings_office_name'),
  lat: () => t('settings_lat'),
  lng: () => t('settings_lng'),
  radius_m: () => t('settings_radius'),
  office_public_ip: () => t('settings_office_ip'),
  play_group_url: () => t('settings_play_group'),
  android_app_required: () => t('settings_app_required'),
  app_required_for_all: () => t('settings_app_required_all'),
  app_install_route: () => t('settings_install_route'),
  day_cutoff_time: () => t('settings_cutoff'),
  digest_time: () => t('settings_digest'),
  daily_report_time: () => t('settings_daily_report'),
  monthly_report_enabled: () => t('settings_monthly_report'),
  punchout_reminder_time: () => t('settings_reminder'),
  shift_times: () => t('settings_shift_times'),
  off_cap_total: () => `${t('settings_caps')} — ${t('settings_cap_total')}`,
  off_cap_full_time: () => `${t('settings_caps')} — ${t('settings_cap_ft')}`,
  off_cap_part_time: () => `${t('settings_caps')} — ${t('settings_cap_pt')}`,
};
const settingLabel = (key: string): string =>
  SETTING_LABELS[key]?.() ??
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function SettingsPage() {
  return (
    <div className="stack">
      <OfficeCard />
      <HolidaysCard />
      <DesignationsCard />
      <AdminsPage />
    </div>
  );
}

function OfficeCard() {
  const [form, setForm] = useState<Settings | null>(null);
  const [saved, setSaved] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'success' | 'info'; text: string } | null>(null);

  /** One end of one window. The rest of the timetable is carried through untouched. */
  const setWindow = (
    type: EmploymentType,
    shift: Shift,
    end: 'start' | 'end',
    value: string,
  ) =>
    setForm((f) =>
      f
        ? {
            ...f,
            shift_times: {
              ...f.shift_times,
              [type]: { ...f.shift_times[type], [shift]: { ...f.shift_times[type][shift], [end]: value } },
            },
          }
        : f,
    );

  useEffect(() => {
    api<{ settings: Settings }>('/admin/settings')
      .then((r) => {
        setForm(r.settings);
        setSaved(r.settings);
      })
      .catch((err) => setMsg({ kind: 'error', text: errorMessage(err) }));
  }, []);

  const pick = useCallback((p: { lat: number; lng: number }) => {
    setForm((f) => (f ? { ...f, lat: p.lat, lng: p.lng } : f));
  }, []);

  if (!form) return <Spinner />;
  const set = <K extends keyof Settings>(k: K, v: Settings[K]) => setForm({ ...form, [k]: v });
  const num = (k: keyof Settings) => (e: React.ChangeEvent<HTMLInputElement>) =>
    set(k, Number(e.target.value) as never);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (form.lat == null || form.lng == null) {
      setMsg({ kind: 'error', text: t('settings_pin_required') });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<{ settings: Settings; changed: string[] }>('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({
          office_name: form.office_name,
          lat: form.lat,
          lng: form.lng,
          radius_m: form.radius_m,
          play_group_url: form.play_group_url?.trim() || null,
          app_install_route: form.app_install_route,
          day_cutoff_time: form.day_cutoff_time,
          digest_time: form.digest_time,
          daily_report_time: form.daily_report_time,
          monthly_report_enabled: form.monthly_report_enabled,
          punchout_reminder_time: form.punchout_reminder_time,
          off_cap_total: form.off_cap_total,
          off_cap_full_time: form.off_cap_full_time,
          off_cap_part_time: form.off_cap_part_time,
        }),
      });
      setForm(r.settings);
      setSaved(r.settings);
      setMsg({
        kind: 'success',
        text: r.changed.length
          ? t('settings_saved', { fields: r.changed.map(settingLabel).join(', ') })
          : t('settings_unchanged'),
      });
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const center =
    form.lat != null && form.lng != null ? { lat: form.lat, lng: form.lng } : DEFAULT_CENTER;
  const office =
    form.lat != null && form.lng != null
      ? { lat: form.lat, lng: form.lng, radius_m: form.radius_m }
      : null;
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  return (
    <section className="card settings-card">
      <h2>{t('nav_settings')}</h2>
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      <form onSubmit={save}>
        <h3>{t('settings_office')}</h3>
        <p className="muted">{t('settings_pin_help')}</p>
        <div className="map-row">
          <MapView
            center={center}
            office={office}
            onPick={pick}
            zoom={office ? 16 : 12}
            height={320}
          />
          <div className="map-side">
            <label>
              {t('settings_office_name')}
              <input
                value={form.office_name}
                onChange={(e) => set('office_name', e.target.value)}
                required
                maxLength={100}
              />
            </label>
            <label>
              {t('settings_lat')}
              <input
                type="number"
                step="0.000001"
                min={-90}
                max={90}
                value={form.lat ?? ''}
                onChange={(e) => set('lat', e.target.value === '' ? null : Number(e.target.value))}
                required
              />
            </label>
            <label>
              {t('settings_lng')}
              <input
                type="number"
                step="0.000001"
                min={-180}
                max={180}
                value={form.lng ?? ''}
                onChange={(e) => set('lng', e.target.value === '' ? null : Number(e.target.value))}
                required
              />
            </label>
            <label>
              {t('settings_radius')}
              <input
                type="number"
                min={20}
                max={5000}
                value={form.radius_m}
                onChange={num('radius_m')}
                required
              />
            </label>
          </div>
        </div>
        {/*
          16 October 2026, the owner: the office IP box, and the two "must punch from the app"
          checkboxes, are gone. The app is always required (migration 0052; an iPhone is excused
          on the employee's own page), and the IP only ever labelled a punch. What is left is the
          one choice that still changes: where the Get The App page sends a new employee.
        */}
        <div className="grid-form">
          <label className="half">
            {t('settings_install_route')}{' '}
            <span className="muted block-help">{t('settings_install_route_help')}</span>
            <select
              value={form.app_install_route}
              onChange={(e) =>
                set('app_install_route', e.target.value as Settings['app_install_route'])
              }
            >
              <option value="play_public">{t('settings_route_play_public')}</option>
              <option value="play_group">{t('settings_route_play_group')}</option>
              {form.app_install_route === 'download' && (
                <option value="download">{t('settings_route_download')}</option>
              )}
            </select>
          </label>
          {form.app_install_route === 'play_group' && (
            <label className="half">
              {t('settings_play_group')}{' '}
              <span className="muted block-help">{t('settings_play_group_help')}</span>
              <input
                value={form.play_group_url ?? ''}
                onChange={(e) => set('play_group_url', e.target.value)}
                maxLength={300}
                inputMode="url"
                placeholder="https://groups.google.com/g/..."
              />
            </label>
          )}
        </div>

        <h3>{t('settings_times')}</h3>
        <div className="grid-form">
          <label>
            {t('settings_cutoff')}{' '}
            <span className="muted block-help">{t('settings_cutoff_help')}</span>
            <TimeField
              value={form.day_cutoff_time}
              label={t('settings_cutoff')}
              onChange={(v) => set('day_cutoff_time', v)}
            />
          </label>
          <label>
            {t('settings_digest')}{' '}
            <span className="muted block-help">{t('settings_digest_help')}</span>
            <TimeField
              value={form.digest_time}
              label={t('settings_digest')}
              onChange={(v) => set('digest_time', v)}
            />
          </label>
          <label>
            {t('settings_daily_report')}{' '}
            <span className="muted block-help">{t('settings_daily_report_help')}</span>
            <TimeField
              value={form.daily_report_time}
              label={t('settings_daily_report')}
              onChange={(v) => set('daily_report_time', v)}
            />
          </label>
          <label className="inline monthly-toggle">
            <input
              type="checkbox"
              checked={form.monthly_report_enabled}
              onChange={(e) => set('monthly_report_enabled', e.target.checked)}
            />
            {t('settings_monthly_report')}
          </label>
        </div>

      {/*
        The timetable, not a rule: nothing in the hours engine reads these. A full-time day is
        nine hours and a part-time one five, whenever they are worked. What these decide is what
        the roster says an employee is expected for — the sheet that used to be kept by hand.
      */}
      <h3>{t('settings_shift_times')}</h3>
        <p className="muted">{t('settings_shift_times_help')}</p>
        <div className="grid-form shift-times">
          {(['full_time', 'part_time'] as const).map((type) =>
            (['first', 'second'] as const).map((shift) => (
              <label key={`${type}-${shift}`}>
                {t(type)} · {shiftLabel(shift)}
                <span className="row time-pair">
                  <input
                    type="time"
                    value={form.shift_times[type][shift].start}
                    onChange={(e) => setWindow(type, shift, 'start', e.target.value)}
                    required
                  />
                  <span className="muted">–</span>
                  <input
                    type="time"
                    value={form.shift_times[type][shift].end}
                    onChange={(e) => setWindow(type, shift, 'end', e.target.value)}
                    required
                  />
                </span>
              </label>
            )),
          )}
        </div>

        <h3>{t('settings_caps')}</h3>
        <p className="muted">{t('settings_caps_help')}</p>
        <div className="grid-form">
          <label>
            {t('settings_cap_total')}
            <input
              type="number"
              min={0}
              max={30}
              value={form.off_cap_total}
              onChange={num('off_cap_total')}
              required
            />
          </label>
          <label>
            {t('settings_cap_ft')}
            <input
              type="number"
              min={0}
              max={30}
              value={form.off_cap_full_time}
              onChange={num('off_cap_full_time')}
              required
            />
          </label>
          <label>
            {t('settings_cap_pt')}
            <input
              type="number"
              min={0}
              max={30}
              value={form.off_cap_part_time}
              onChange={num('off_cap_part_time')}
              required
            />
          </label>
        </div>

        <div className="row save-bar">
          <button type="submit" disabled={busy || !dirty}>
            {t('save')}
          </button>
          <span className="muted">
            {dirty
              ? t('settings_unsaved')
              : saved
                ? t('settings_last_saved', { date: fmtDate(saved.updated_at) })
                : ''}
          </span>
        </div>
      </form>
    </section>
  );
}

function HolidaysCard() {
  const confirm = useConfirm();
  const [holidays, setHolidays] = useState<Holiday[] | null>(null);
  const [form, setForm] = useState({ date: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setHolidays((await api<{ holidays: Holiday[] }>('/admin/holidays')).holidays);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/admin/holidays', form);
      setForm({ date: '', name: '' });
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'holiday_exists'
          ? t('holiday_exists')
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (h: Holiday) => {
    if (
      !(await confirm({
        title: t('remove'),
        body: t('holiday_remove_confirm', { name: h.name, date: fmtDate(h.date, false) }),
        confirmLabel: t('remove'),
        danger: true,
      }))
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/holidays/${h.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h3>{t('holidays_title')}</h3>
      <p className="muted">{t('holidays_intro')}</p>
      {error && <Alert>{error}</Alert>}
      <form onSubmit={add} className="grid-form">
        <label>
          {t('holiday_date')}
          <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />
        </label>
        <label>
          {t('holiday_name')}
          <input
            value={form.name}
            placeholder={t('holiday_name_placeholder')}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            maxLength={100}
          />
        </label>
        <div className="row">
          <button type="submit" disabled={busy}>
            {t('holiday_add')}
          </button>
        </div>
      </form>
      {!holidays ? (
        <Spinner />
      ) : holidays.length === 0 ? (
        <p className="muted">{t('holidays_none')}</p>
      ) : (
        <ul className="list">
          {holidays.map((h) => (
            <li key={h.id} className="row between">
              <span>
                <strong>{fmtDate(h.date, false)}</strong> · {h.name}
              </span>
              <button
                type="button"
                className="small secondary"
                onClick={() => remove(h)}
                disabled={busy}
              >
                {t('holiday_remove')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
